import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Swal from 'sweetalert2';
import { FiUploadCloud, FiX, FiFile, FiCheck, FiCheckSquare, FiImage, FiPlus, FiMinus } from 'react-icons/fi';

const CompanyDetailsForm = () => {
  const navigate = useNavigate();

  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [contactStates, setContactStates] = useState([]);
  const [contactCities, setContactCities] = useState([]);
  const [branchStates, setBranchStates] = useState([]);
  const [branchCities, setBranchCities] = useState([]);
  const [diagnosticCenters, setDiagnosticCenters] = useState([]);
  const [loading, setLoading] = useState({
    countries: false,
    states: false,
    cities: false,
    diagnosticCenters: false,
    contactStates: false,
    contactCities: false,
    branchStates: false,
    branchCities: false
  });
  const [showDiagnosticCenters, setShowDiagnosticCenters] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const initialFormData = {
    name: '',
    companyType: '',
    assignedBy: '',
    registrationDate: '',
    contractPeriod: '',
    renewalDate: '',
    insuranceBroker: '',
    email: '',
    password: '',
    phone: '',
    gstNumber: '',
    companyStrength: '',
    noOfBranches: '', 
    country: '',
    state: '',
    city: '',
    pincode: '',
    diagnostic: [],
    documents: [],
    image: null
  };

  const industryOptions = [
    "Agriculture & Food Processing",
    "Banking, Financial Services & Insurance (BFSI)",
    "Consulting & Professional Services",
    "Education & Training",
    "Energy & Utilities",
    "Government & Public Sector",
    "Healthcare & Pharmaceuticals",
    "Hospitality & Tourism",
    "Information Technology & Software",
    "Manufacturing & Engineering",
    "Media & Entertainment",
    "Non-Governmental Organizations (NGOs)",
    "Real Estate & Construction",
    "Retail & E-commerce",
    "Telecommunications",
    "Transportation & Logistics"
  ];

  const initialContactPersons = [{
    name: '', designation: '', gender: '', email: '', phone: '',
    address: { country: '', state: '', city: '', pincode: '', street: 'Not Provided' }
  }];

  const initialBranch = {
    branchName: '',
    branchCode: '',
    email: '',
    phone: '',
    branchHead: '',
    country: '',
    state: '',
    city: '',
    pincode: '',
    address: '',
    contactPerson: {
      name: '',
      designation: '',
      email: '',
      phone: '',
      gender: ''
    }
  };

  const [formData, setFormData] = useState(initialFormData);
  const [contactPersons, setContactPersons] = useState(initialContactPersons);
  const [branches, setBranches] = useState([initialBranch]);

  const resetForm = () => {
    setFormData(initialFormData);
    setContactPersons(initialContactPersons);
    setBranches([initialBranch]);
    setContactStates([]);
    setContactCities([]);
    setBranchStates([]);
    setBranchCities([]);
    setShowDiagnosticCenters(false);
  };

  // Fetch countries on component mount
  useEffect(() => {
    setLoading(l => ({ ...l, countries: true }));
    fetch('https://countriesnow.space/api/v0.1/countries/iso')
      .then(res => res.json())
      .then(data => {
        if (data.data && Array.isArray(data.data)) {
          const countryNames = data.data.map(c => c.name).sort();
          setCountries(countryNames);
        } else {
          setCountries(['India', 'United States', 'Canada']);
        }
      })
      .catch(err => {
        console.error("Country fetch error", err);
        setCountries(['India', 'United States', 'Canada']);
      })
      .finally(() => setLoading(l => ({ ...l, countries: false })));
  }, []);

  // Fetch diagnostic centers with pincodes
  useEffect(() => {
    setLoading(l => ({ ...l, diagnosticCenters: true }));
    axios.get("https://api.elthiumhealth.com/api/admin/alldiagnostics")
      .then(response => {
        const centers = response?.data?.data;
        if (Array.isArray(centers)) {
          setDiagnosticCenters(centers.map(center => ({
            id: center._id || "",
            name: center.name || "Unnamed Center",
            pincode: center.pincode || ""
          })));
        } else {
          setDiagnosticCenters([
            { id: '1', name: 'Hospital A', pincode: '110001' },
            { id: '2', name: 'Hospital B', pincode: '400001' },
            { id: '3', name: 'Clinic C', pincode: '560001' }
          ]);
        }
      })
      .catch(err => {
        console.error("Diagnostic centers fetch error:", err);
        setDiagnosticCenters([
          { id: '1', name: 'Hospital A', pincode: '110001' },
          { id: '2', name: 'Hospital B', pincode: '400001' },
          { id: '3', name: 'Clinic C', pincode: '560001' }
        ]);
      })
      .finally(() => {
        setLoading(l => ({ ...l, diagnosticCenters: false }));
      });
  }, []);

  // Fetch states for company address
  const fetchStates = async (country) => {
    if (!country) return;

    setLoading(l => ({ ...l, states: true }));
    setStates([]);
    setCities([]);

    try {
      const response = await axios.post('https://countriesnow.space/api/v0.1/countries/states', {
        country
      });

      if (response.data?.data?.states) {
        setStates(response.data.data.states.map(s => s.name));
      }
    } catch (error) {
      console.error("Error fetching states:", error);
    } finally {
      setLoading(l => ({ ...l, states: false }));
    }
  };

  // Fetch cities for company address
  const fetchCities = async (country, state) => {
    if (!country || !state) return;

    setLoading(l => ({ ...l, cities: true }));
    setCities([]);

    try {
      const response = await axios.post('https://countriesnow.space/api/v0.1/countries/state/cities', {
        country,
        state
      });

      if (response.data?.data) {
        setCities(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching cities:", error);
    } finally {
      setLoading(l => ({ ...l, cities: false }));
    }
  };

  // Fetch states for contact person address
  const fetchContactStates = async (country, contactIndex) => {
    if (!country) return;

    setContactStates(prev => {
      const newStates = [...prev];
      newStates[contactIndex] = [];
      return newStates;
    });

    setContactCities(prev => {
      const newCities = [...prev];
      newCities[contactIndex] = [];
      return newCities;
    });

    try {
      const response = await axios.post('https://countriesnow.space/api/v0.1/countries/states', {
        country
      });

      if (response.data?.data?.states) {
        const stateNames = response.data.data.states.map(s => s.name);
        setContactStates(prev => {
          const newStates = [...prev];
          newStates[contactIndex] = stateNames;
          return newStates;
        });
      }
    } catch (error) {
      console.error("Error fetching states:", error);
    }
  };

  // Fetch cities for contact person address
  const fetchContactCities = async (country, state, contactIndex) => {
    if (!country || !state) return;

    setContactCities(prev => {
      const newCities = [...prev];
      newCities[contactIndex] = [];
      return newCities;
    });

    try {
      const response = await axios.post('https://countriesnow.space/api/v0.1/countries/state/cities', {
        country,
        state
      });

      if (response.data?.data) {
        setContactCities(prev => {
          const newCities = [...prev];
          newCities[contactIndex] = response.data.data;
          return newCities;
        });
      }
    } catch (error) {
      console.error("Error fetching cities:", error);
    }
  };

  // Fetch states for branch address
  const fetchBranchStates = async (country, branchIndex) => {
    if (!country) return;

    setBranchStates(prev => {
      const newStates = [...prev];
      newStates[branchIndex] = [];
      return newStates;
    });

    setBranchCities(prev => {
      const newCities = [...prev];
      newCities[branchIndex] = [];
      return newCities;
    });

    try {
      const response = await axios.post('https://countriesnow.space/api/v0.1/countries/states', {
        country
      });

      if (response.data?.data?.states) {
        const stateNames = response.data.data.states.map(s => s.name);
        setBranchStates(prev => {
          const newStates = [...prev];
          newStates[branchIndex] = stateNames;
          return newStates;
        });
      }
    } catch (error) {
      console.error("Error fetching branch states:", error);
    }
  };

  // Fetch cities for branch address
  const fetchBranchCities = async (country, state, branchIndex) => {
    if (!country || !state) return;

    setBranchCities(prev => {
      const newCities = [...prev];
      newCities[branchIndex] = [];
      return newCities;
    });

    try {
      const response = await axios.post('https://countriesnow.space/api/v0.1/countries/state/cities', {
        country,
        state
      });

      if (response.data?.data) {
        setBranchCities(prev => {
          const newCities = [...prev];
          newCities[branchIndex] = response.data.data;
          return newCities;
        });
      }
    } catch (error) {
      console.error("Error fetching branch cities:", error);
    }
  };

  const handleInputChange = e => {
    const { name, value } = e.target;
    setFormData(fd => ({ ...fd, [name]: value }));

    if (name === 'country') {
      fetchStates(value);
    } else if (name === 'state') {
      fetchCities(formData.country, value);
    }
  };

  const handleContactChange = (idx, field, value, nested) => {
    const c = [...contactPersons];
    if (nested) {
      c[idx][field][nested] = value;

      if (nested === 'country') {
        fetchContactStates(value, idx);
      } else if (nested === 'state') {
        fetchContactCities(c[idx].address.country, value, idx);
      }
    } else {
      c[idx][field] = value;
    }
    setContactPersons(c);
  };

  const handleBranchChange = (branchIndex, field, value, nested = false) => {
    const updatedBranches = [...branches];
    
    if (nested) {
      if (field === 'contactPerson') {
        updatedBranches[branchIndex].contactPerson[nested] = value;
      } else {
        updatedBranches[branchIndex][field][nested] = value;
      }
    } else {
      updatedBranches[branchIndex][field] = value;
    }

    setBranches(updatedBranches);

    // Fetch states/cities when country/state changes
    if (field === 'country' && !nested) {
      fetchBranchStates(value, branchIndex);
    } else if (field === 'state' && !nested) {
      fetchBranchCities(updatedBranches[branchIndex].country, value, branchIndex);
    }
  };

  const handleAddContact = () => {
    setContactPersons([...contactPersons, {
      name: '', designation: '', gender: '', email: '', phone: '',
      address: { country: '', state: '', city: '', pincode: '', street: 'Not Provided' }
    }]);
    setContactStates([...contactStates, []]);
    setContactCities([...contactCities, []]);
  };

  const handleRemoveContact = idx => {
    const c = [...contactPersons];
    c.splice(idx, 1);
    setContactPersons(c);

    const cs = [...contactStates];
    cs.splice(idx, 1);
    setContactStates(cs);

    const cc = [...contactCities];
    cc.splice(idx, 1);
    setContactCities(cc);
  };

  const handleAddBranch = () => {
    setBranches([...branches, { ...initialBranch }]);
    setBranchStates([...branchStates, []]);
    setBranchCities([...branchCities, []]);
  };

  const handleRemoveBranch = (branchIndex) => {
    const updatedBranches = [...branches];
    updatedBranches.splice(branchIndex, 1);
    setBranches(updatedBranches);

    const updatedStates = [...branchStates];
    updatedStates.splice(branchIndex, 1);
    setBranchStates(updatedStates);

    const updatedCities = [...branchCities];
    updatedCities.splice(branchIndex, 1);
    setBranchCities(updatedCities);
  };

  const handleFileUpload = e => {
    const files = Array.from(e.target.files);
    if (e.target.name === 'image') {
      setFormData(fd => ({ ...fd, image: files[0] }));
    } else {
      setFormData(fd => ({ ...fd, documents: files.slice(0, 5) }));
    }
  };

  const removeDocument = (index) => {
    setFormData(fd => {
      const newDocs = [...fd.documents];
      newDocs.splice(index, 1);
      return { ...fd, documents: newDocs };
    });
  };

  const removeImage = () => {
    setFormData(fd => ({ ...fd, image: null }));
  };

  // Filter diagnostic centers by name or pincode
  const filteredCenters = diagnosticCenters.filter(center => {
    const search = searchTerm.toLowerCase();
    return (
      center.name.toLowerCase().includes(search) ||
      center.pincode.toString().includes(search)
    );
  });

  const toggleDiagnosticCenter = (id) => {
    setFormData(fd => ({
      ...fd,
      diagnostic: fd.diagnostic.includes(id)
        ? fd.diagnostic.filter(d => d !== id)
        : [...fd.diagnostic, id]
    }));
  };

  const selectAllDiagnosticCenters = () => {
    setFormData(fd => ({
      ...fd,
      diagnostic: fd.diagnostic.length === filteredCenters.length
        ? []
        : filteredCenters.map(c => c.id)
    }));
  };

  const toggleDiagnosticCentersDropdown = () => {
    setShowDiagnosticCenters(!showDiagnosticCenters);
  };

  const validateForm = () => {
    const required = ["name", "companyType", "assignedBy", "registrationDate",
      "contractPeriod", "renewalDate", "insuranceBroker", "email",
      "password", "phone", "companyStrength",
      "country", "state", "city", "pincode"];
    const missing = required.filter(f => !formData[f]);
    if (missing.length) {
      Swal.fire("Missing Fields", `Please fill:<br>${missing.join("<br>")}`, "warning");
      return false;
    }
    if (formData.diagnostic.length === 0) {
      Swal.fire("Error", "Select at least one diagnostic center", "error");
      return false;
    }
    if (contactPersons.length === 0) {
      Swal.fire("Error", "Add at least one contact person", "error");
      return false;
    }
    if (branches.length === 0) {
      Swal.fire("Error", "Add at least one branch", "error");
      return false;
    }
    return true;
  };

  const handleSubmit = async e => {
  e.preventDefault();
  if (!validateForm()) return;

  const payload = new FormData();

  // Append all form data
  Object.entries(formData).forEach(([key, value]) => {
    if (key === 'diagnostic') {
      payload.append(key, JSON.stringify(value));
    } else if (key !== 'documents' && key !== 'image' && key !== 'contactPerson') {
      payload.append(key, value);
    }
  });

  // Append contact persons
  payload.append('contactPerson', JSON.stringify(contactPersons));

  // Append branches
  payload.append('branches', JSON.stringify(branches));

  // Append image
  if (formData.image) {
    payload.append('image', formData.image);
  }

  // Append documents
  formData.documents.forEach(f => payload.append('documents', f));

  try {
    const res = await axios.post("https://api.elthiumhealth.com/api/admin/create-company", payload, {
      headers: { "Content-Type": "multipart/form-data" }
    });
    
    // ✅ BEST FIX: Check for success status instead of specific message
    if (res.status === 201) {
      Swal.fire("Success", "Company created successfully", "success");
      navigate("/companylist");
    } else {
      throw new Error(res.data.message || "Creation failed");
    }
  } catch (err) {
    console.error(err);
    // ✅ Better error handling
    const errorMessage = err.response?.data?.message || err.message || "Something went wrong";
    Swal.fire("Error", errorMessage, "error");
  }
};
  const handleCancel = () => {
    resetForm();
    navigate('/company-register');
  };

  const [showOtherInput, setShowOtherInput] = useState(false);
  const [otherIndustry, setOtherIndustry] = useState("");

  const handleSelectChange = (e) => {
    const value = e.target.value;
    if (value === "Others") {
      setShowOtherInput(true);
      setFormData({ ...formData, companyType: "" });
    } else {
      setShowOtherInput(false);
      setFormData({ ...formData, companyType: value });
      setOtherIndustry("");
    }
  };

  const handleOtherInputChange = (e) => {
    setOtherIndustry(e.target.value);
    setFormData({ ...formData, companyType: e.target.value });
  };

  return (
    <div className="p-6 bg-white rounded shadow max-w-6xl mx-auto">
      <h3 className="text-2xl font-bold mb-6 text-center text-green-700">Create Company</h3>
      <form onSubmit={handleSubmit} className="space-y-6">

        {/* Company Basic Info */}
        <div className="bg-gray-50 p-4 rounded-lg">
          <h4 className="text-lg font-semibold mb-4 text-gray-800">Company Basic Information</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Company Name</label>
              <input name="name" value={formData.name} onChange={handleInputChange} required className="border p-2 rounded w-full" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Industry Type</label>
              <select
                name="companyType"
                value={showOtherInput ? "Others" : formData.companyType}
                onChange={handleSelectChange}
                required
                className="border p-2 rounded w-full"
              >
                <option value="">Select Industry Type</option>
                {industryOptions.map((industry) => (
                  <option key={industry} value={industry}>
                    {industry}
                  </option>
                ))}
                <option value="Others">Others</option>
              </select>

              {showOtherInput && (
                <input
                  type="text"
                  placeholder="Please specify your industry"
                  value={otherIndustry}
                  onChange={handleOtherInputChange}
                  required
                  className="mt-2 border p-2 rounded w-full"
                />
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Contact Person</label>
              <input name="assignedBy" value={formData.assignedBy} onChange={handleInputChange} required className="border p-2 rounded w-full" />
            </div>
          </div>

          {/* Dates and Contract Info */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Empanelment</label>
              <input type="date" name="registrationDate" value={formData.registrationDate} onChange={handleInputChange} required className="border p-2 rounded w-full" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Contract Period (Years)</label>
              <select name="contractPeriod" value={formData.contractPeriod} onChange={handleInputChange} required className="border p-2 rounded w-full">
                <option value="">Select Contract Period</option>
                <option>1</option>
                <option>2</option>
                <option>3</option>
                <option>4</option>
                <option>5</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Renewal Date</label>
              <input type="date" name="renewalDate" value={formData.renewalDate} onChange={handleInputChange} required className="border p-2 rounded w-full" />
            </div>
          </div>

          {/* Insurance and Contact Info */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Insurance Partner</label>
              <input name="insuranceBroker" value={formData.insuranceBroker} onChange={handleInputChange} required className="border p-2 rounded w-full" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Email</label>
              <input type="email" name="email" value={formData.email} onChange={handleInputChange} required className="border p-2 rounded w-full" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Password</label>
              <input type="password" name="password" value={formData.password} onChange={handleInputChange} required className="border p-2 rounded w-full" />
            </div>
          </div>

          {/* Additional Info */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Phone</label>
              <input type="tel" name="phone" value={formData.phone} onChange={handleInputChange} required className="border p-2 rounded w-full" />
            </div>

             <div>
    <label className="block text-sm font-medium text-gray-700">Number of Branches</label>
    <input 
      type="number" 
      name="noOfBranches" 
      value={formData.noOfBranches} 
      onChange={handleInputChange} 
      required 
      className="border p-2 rounded w-full"
      min="1"
    />
  </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">EMP Head Count</label>
              <input type="number" name="companyStrength" value={formData.companyStrength} onChange={handleInputChange} required className="border p-2 rounded w-full" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">GST Number</label>
              <input name="gstNumber" value={formData.gstNumber} onChange={handleInputChange} className="border p-2 rounded w-full" />
            </div>
          </div>

          {/* Location */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Country</label>
              <select name="country" value={formData.country} onChange={handleInputChange} required className="border p-2 rounded w-full">
                <option value="">Select Country</option>
                {countries.map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">State</label>
              <select name="state" value={formData.state} onChange={handleInputChange} required className="border p-2 rounded w-full" disabled={!formData.country}>
                <option value="">Select State</option>
                {states.map(s => <option key={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">City</label>
              <select name="city" value={formData.city} onChange={handleInputChange} required className="border p-2 rounded w-full" disabled={!formData.state}>
                <option value="">Select City</option>
                {cities.map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
          </div>

          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700">Pincode</label>
            <input name="pincode" value={formData.pincode} onChange={handleInputChange} required className="border p-2 rounded w-full" />
          </div>
        </div>

        {/* Company Image Upload */}
        <div className="bg-gray-50 p-4 rounded-lg">
          <h4 className="text-lg font-semibold mb-4 text-gray-800">Company Logo & Documents</h4>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">Company Logo/Image</label>
            {formData.image ? (
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded">
                <div className="flex items-center">
                  <FiImage className="text-gray-500 mr-2" />
                  <span className="text-sm text-gray-700">{formData.image.name}</span>
                </div>
                <button
                  type="button"
                  onClick={removeImage}
                  className="text-red-500 hover:text-red-700"
                >
                  <FiX />
                </button>
              </div>
            ) : (
              <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-blue-500 hover:bg-blue-50 transition-colors">
                <div className="flex flex-col items-center justify-center pt-5 pb-6">
                  <FiUploadCloud className="w-8 h-8 text-gray-500 mb-2" />
                  <p className="text-sm text-gray-500">
                    <span className="font-semibold">Click to upload</span> company image
                  </p>
                </div>
                <input
                  type="file"
                  name="image"
                  accept="image/*"
                  onChange={handleFileUpload}
                  className="hidden"
                />
              </label>
            )}
          </div>

          {/* Documents Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Upload Company Documents (Max 5)</label>
            <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-blue-500 hover:bg-blue-50 transition-colors">
              <div className="flex flex-col items-center justify-center pt-5 pb-6">
                <FiUploadCloud className="w-8 h-8 text-gray-500 mb-2" />
                <p className="text-sm text-gray-500">
                  <span className="font-semibold">Click to upload</span> or drag and drop
                </p>
                <p className="text-xs text-gray-500">PDF, DOC, JPG, PNG (Max 5 files)</p>
              </div>
              <input
                type="file"
                multiple
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                onChange={handleFileUpload}
                className="hidden"
              />
            </label>

            {formData.documents.length > 0 && (
              <div className="mt-4 space-y-2">
                {formData.documents.map((file, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded">
                    <div className="flex items-center">
                      <FiFile className="text-gray-500 mr-2" />
                      <span className="text-sm text-gray-700 truncate max-w-xs">{file.name}</span>
                      <span className="text-xs text-gray-500 ml-2">
                        {(file.size / 1024).toFixed(1)} KB
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeDocument(index)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <FiX />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Diagnostic Centers Section */}
        <div className="bg-gray-50 p-4 rounded-lg">
          <h4 className="text-lg font-semibold mb-4 text-gray-800">Diagnostic Centers</h4>
          <div className="relative">
            <button
              type="button"
              onClick={toggleDiagnosticCentersDropdown}
              className="w-full flex justify-between items-center border p-2 rounded bg-white"
            >
              <span>
                {formData.diagnostic.length > 0
                  ? `${formData.diagnostic.length} selected`
                  : "Select Diagnostic Centers"}
              </span>
              <svg
                className="h-5 w-5 text-gray-400"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
            </button>

            {showDiagnosticCenters && (
              <div className="absolute z-10 mt-1 w-full bg-white shadow-lg max-h-72 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm">
                {/* Search Input */}
                <div className="p-2 border-b">
                  <input
                    type="text"
                    placeholder="Search by name or pincode"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full px-3 py-1 border rounded text-sm"
                  />
                </div>

                {/* Select All */}
                <div
                  className="flex items-center px-4 py-2 hover:bg-gray-100 cursor-pointer"
                  onClick={selectAllDiagnosticCenters}
                >
                  {formData.diagnostic.length === filteredCenters.length &&
                    filteredCenters.length > 0 ? (
                    <FiCheckSquare className="text-blue-500 mr-2" />
                  ) : (
                    <FiCheck className="text-gray-400 mr-2" />
                  )}
                  <span>Select All</span>
                </div>

                {/* Centers List */}
                {filteredCenters.length > 0 ? (
                  filteredCenters.map((center) => (
                    <div
                      key={center.id}
                      className="flex items-center px-4 py-2 hover:bg-gray-100 cursor-pointer"
                      onClick={() => toggleDiagnosticCenter(center.id)}
                    >
                      {formData.diagnostic.includes(center.id) ? (
                        <FiCheckSquare className="text-blue-500 mr-2" />
                      ) : (
                        <FiCheck className="text-gray-400 mr-2" />
                      )}
                      <div>
                        <div className="font-medium">{center.name}</div>
                        <div className="text-xs text-gray-500">Pincode: {center.pincode}</div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="px-4 py-2 text-gray-500 text-sm">No results found.</div>
                )}
              </div>
            )}
          </div>

          {/* Selected Chips */}
          <div className="flex flex-wrap gap-2 mt-3">
            {formData.diagnostic.map((centerId) => {
              const center = diagnosticCenters.find((c) => c.id === centerId);
              return (
                <div
                  key={centerId}
                  className="flex items-center bg-blue-100 text-blue-800 rounded-full px-3 py-1 text-sm"
                >
                  {center?.name || "Unknown Center"} ({center?.pincode || "N/A"})
                  <button
                    type="button"
                    onClick={() => toggleDiagnosticCenter(centerId)}
                    className="ml-2 text-blue-600 hover:text-blue-900"
                  >
                    <FiX size={14} />
                  </button>
                </div>
              );
            })}
          </div>
        </div>

        {/* Branches Section */}
        <div className="bg-gray-50 p-4 rounded-lg">
          <div className="flex justify-between items-center mb-4">
            <h4 className="text-lg font-semibold text-gray-800">Branches</h4>
            <button
              type="button"
              onClick={handleAddBranch}
              className="flex items-center bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
            >
              <FiPlus className="mr-2" />
              Add Branch
            </button>
          </div>

          {branches.map((branch, branchIndex) => (
            <div key={branchIndex} className="border border-gray-200 rounded-lg p-4 mb-4 bg-white">
              <div className="flex justify-between items-center mb-4">
                <h5 className="font-semibold text-gray-700">Branch {branchIndex + 1}</h5>
                {branches.length > 1 && (
                  <button
                    type="button"
                    onClick={() => handleRemoveBranch(branchIndex)}
                    className="text-red-600 hover:text-red-800"
                  >
                    <FiMinus />
                  </button>
                )}
              </div>

              {/* Branch Basic Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Branch Name</label>
                  <input
                    value={branch.branchName}
                    onChange={(e) => handleBranchChange(branchIndex, 'branchName', e.target.value)}
                    className="border p-2 rounded w-full"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Branch Code</label>
                  <input
                    value={branch.branchCode}
                    onChange={(e) => handleBranchChange(branchIndex, 'branchCode', e.target.value)}
                    className="border p-2 rounded w-full"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Branch Email</label>
                  <input
                    type="email"
                    value={branch.email}
                    onChange={(e) => handleBranchChange(branchIndex, 'email', e.target.value)}
                    className="border p-2 rounded w-full"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Branch Phone</label>
                  <input
                    type="tel"
                    value={branch.phone}
                    onChange={(e) => handleBranchChange(branchIndex, 'phone', e.target.value)}
                    className="border p-2 rounded w-full"
                    required
                  />
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700">Branch Head</label>
                <input
                  value={branch.branchHead}
                  onChange={(e) => handleBranchChange(branchIndex, 'branchHead', e.target.value)}
                  className="border p-2 rounded w-full"
                  required
                />
              </div>

              {/* Branch Address */}
              <h6 className="font-medium mb-2 text-gray-700">Branch Address</h6>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Country</label>
                  <select
                    value={branch.country}
                    onChange={(e) => handleBranchChange(branchIndex, 'country', e.target.value)}
                    className="border p-2 rounded w-full"
                    required
                  >
                    <option value="">Select Country</option>
                    {countries.map(c => (
                      <option key={`branch-${branchIndex}-${c}`} value={c}>{c}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">State</label>
                  <select
                    value={branch.state}
                    onChange={(e) => handleBranchChange(branchIndex, 'state', e.target.value)}
                    className="border p-2 rounded w-full"
                    disabled={!branch.country}
                    required
                  >
                    <option value="">Select State</option>
                    {branchStates[branchIndex]?.map(s => (
                      <option key={`branch-${branchIndex}-${s}`} value={s}>{s}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">City</label>
                  <select
                    value={branch.city}
                    onChange={(e) => handleBranchChange(branchIndex, 'city', e.target.value)}
                    className="border p-2 rounded w-full"
                    disabled={!branch.state}
                    required
                  >
                    <option value="">Select City</option>
                    {branchCities[branchIndex]?.map(c => (
                      <option key={`branch-${branchIndex}-${c}`} value={c}>{c}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Pincode</label>
                  <input
                    value={branch.pincode}
                    onChange={(e) => handleBranchChange(branchIndex, 'pincode', e.target.value)}
                    className="border p-2 rounded w-full"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Full Address</label>
                  <input
                    value={branch.address}
                    onChange={(e) => handleBranchChange(branchIndex, 'address', e.target.value)}
                    className="border p-2 rounded w-full"
                    required
                  />
                </div>
              </div>

              {/* Branch Contact Person */}
              <h6 className="font-medium mb-2 text-gray-700">Branch Contact Person</h6>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Name</label>
                  <input
                    value={branch.contactPerson.name}
                    onChange={(e) => handleBranchChange(branchIndex, 'contactPerson', e.target.value, 'name')}
                    className="border p-2 rounded w-full"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Designation</label>
                  <input
                    value={branch.contactPerson.designation}
                    onChange={(e) => handleBranchChange(branchIndex, 'contactPerson', e.target.value, 'designation')}
                    className="border p-2 rounded w-full"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Email</label>
                  <input
                    type="email"
                    value={branch.contactPerson.email}
                    onChange={(e) => handleBranchChange(branchIndex, 'contactPerson', e.target.value, 'email')}
                    className="border p-2 rounded w-full"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Phone</label>
                  <input
                    type="tel"
                    value={branch.contactPerson.phone}
                    onChange={(e) => handleBranchChange(branchIndex, 'contactPerson', e.target.value, 'phone')}
                    className="border p-2 rounded w-full"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Gender</label>
                <select
                  value={branch.contactPerson.gender}
                  onChange={(e) => handleBranchChange(branchIndex, 'contactPerson', e.target.value, 'gender')}
                  className="border p-2 rounded w-full"
                  required
                >
                  <option value="">Select Gender</option>
                  <option>Male</option>
                  <option>Female</option>
                  <option>Other</option>
                </select>
              </div>
            </div>
          ))}
        </div>

        {/* Contact Persons */}
        <div className="bg-gray-50 p-4 rounded-lg">
          <h4 className="text-lg font-semibold mb-4 text-gray-800">Company Contact Persons</h4>
          {contactPersons.map((cp, idx) => (
            <div key={idx} className="border border-gray-200 rounded-lg p-4 mb-4 bg-white">
              <div className="flex justify-between items-center mb-4">
                <h5 className="font-semibold text-gray-700">Contact Person {idx + 1}</h5>
                {contactPersons.length > 1 && (
                  <button type="button" onClick={() => handleRemoveContact(idx)} className="text-red-600 hover:text-red-800 text-sm">
                    Remove
                  </button>
                )}
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Name</label>
                  <input value={cp.name} onChange={e => handleContactChange(idx, 'name', e.target.value)} className="border p-2 rounded w-full" required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Designation</label>
                  <input value={cp.designation} onChange={e => handleContactChange(idx, 'designation', e.target.value)} className="border p-2 rounded w-full" required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Gender</label>
                  <select value={cp.gender} onChange={e => handleContactChange(idx, 'gender', e.target.value)} className="border p-2 rounded w-full" required>
                    <option value="">Select Gender</option>
                    <option>Male</option>
                    <option>Female</option>
                    <option>Other</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Email</label>
                  <input type="email" value={cp.email} onChange={e => handleContactChange(idx, 'email', e.target.value)} className="border p-2 rounded w-full" required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Phone</label>
                  <input type="tel" value={cp.phone} onChange={e => handleContactChange(idx, 'phone', e.target.value)} className="border p-2 rounded w-full" required />
                </div>
              </div>

              <h5 className="font-medium mb-2 text-gray-700">Address</h5>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Country</label>
                  <select
                    value={cp.address.country}
                    onChange={e => handleContactChange(idx, 'address', e.target.value, 'country')}
                    className="border p-2 rounded w-full"
                  >
                    <option value="">Select Country</option>
                    {countries.map(c => (
                      <option key={`${idx}-${c}`} value={c}>{c}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">State</label>
                  <select
                    value={cp.address.state}
                    onChange={e => handleContactChange(idx, 'address', e.target.value, 'state')}
                    className="border p-2 rounded w-full"
                    disabled={!cp.address.country}
                  >
                    <option value="">Select State</option>
                    {contactStates[idx]?.map(s => (
                      <option key={`${idx}-${s}`} value={s}>{s}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">City</label>
                  <select
                    value={cp.address.city}
                    onChange={e => handleContactChange(idx, 'address', e.target.value, 'city')}
                    className="border p-2 rounded w-full"
                    disabled={!cp.address.state}
                  >
                    <option value="">Select City</option>
                    {contactCities[idx]?.map(c => (
                      <option key={`${idx}-${c}`} value={c}>{c}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Pincode</label>
                  <input value={cp.address.pincode} onChange={e => handleContactChange(idx, 'address', e.target.value, 'pincode')} className="border p-2 rounded w-full" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Street</label>
                  <input value={cp.address.street} onChange={e => handleContactChange(idx, 'address', e.target.value, 'street')} className="border p-2 rounded w-full" />
                </div>
              </div>
            </div>
          ))}
          <button type="button" onClick={handleAddContact} className="bg-blue-100 px-4 py-2 rounded text-blue-800 hover:bg-blue-200 flex items-center">
            <FiPlus className="mr-2" />
            Add Contact Person
          </button>
        </div>

        {/* Form Actions */}
        <div className="flex justify-end gap-4 pt-4">
          <button type="button" onClick={handleCancel} className="bg-gray-200 hover:bg-gray-300 px-6 py-2 rounded text-gray-800">Cancel</button>
          <button type="submit" className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded">Submit</button>
        </div>
      </form>
    </div>
  );
};

export default CompanyDetailsForm;